// @flow
import {default as extractText} from 'lodash/get';
import moment from 'moment-tz';

import type {CreateNewDocProps} from 'app/general/types';
import * as urls from 'app/urls';
import {PROCESS_VERSIONS_API} from 'app/urls';
import * as request from 'app/utils/request';
import {
  createErrorHandler,
  get,
  patchJson,
  showForbiddenMessageHandler,
} from 'app/utils/request';
import {gt} from 'app/utils/i18n';
import {limitRequests} from 'app/utils/redux';
import {
  showNotification,
  showUpgradeModal,
  UPGRADE_MODALS,
} from 'app/toasts/slice';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';

import {getDescription} from 'app/procedures/utils';

import type {
  ProcessDashboardFetchParamsType,
  ProcessDashboardLocationParamsType,
  ProcessType,
  ProcessVersionType,
} from './types';
import type {ResponseType} from 'app/utils/redux_v2';
import {type contentType} from 'app/utils/content-type';

export const REQUEST_PROCESS_LIST = 'REQUEST_PROCESS_LIST';
export const RECEIVE_PROCESS_LIST = 'RECEIVE_PROCESS_LIST';

export const RECEIVE_PROCESS_DETAIL = 'RECEIVE_PROCESS_DETAIL';

export const REQUEST_PROCESS_DASHBOARD_LIST = 'REQUEST_PROCESS_DASHBOARD_LIST';
export const RECEIVE_PROCESS_DASHBOARD_LIST = 'RECEIVE_PROCESS_DASHBOARD_LIST';

export const TOGGLE_PROCESS_DASHBOARD_RELATION =
  'TOGGLE_PROCESS_DASHBOARD_RELATION';
export const INVALIDATE_PROCESS_LIST = 'INVALIDATE_PROCESS_LIST';

export const requestProcessDashboardList = (
  currentFilters: ProcessDashboardLocationParamsType
) => ({
  type: REQUEST_PROCESS_DASHBOARD_LIST,
  currentFilters: {...currentFilters},
});

export const receiveProcessDashboardList = (
  query: ProcessDashboardFetchParamsType,
  response: ResponseType<ProcessType>
) => ({
  type: RECEIVE_PROCESS_DASHBOARD_LIST,
  query,
  response,
});

export const toggleProcessDashboardRelation = (relation: contentType) => ({
  type: TOGGLE_PROCESS_DASHBOARD_RELATION,
  relation,
});

// toggle process side diagram

export const SET_PROCESS_SIDE_DIAGRAM_STATE = 'SET_PROCESS_SIDE_DIAGRAM_STATE';

export const setProcessSideDiagramState = (
  processId: number,
  closed: boolean
) => ({
  type: SET_PROCESS_SIDE_DIAGRAM_STATE,
  processId,
  closed,
});

export const receiveProcessDetail = (item: ProcessType) => ({
  type: RECEIVE_PROCESS_DETAIL,
  item: {
    ...item,
    description: getDescription(item),
  },
});

export const requestProcesses = (
  params?: any
): Promise<ResponseType<ProcessType>> =>
  request.get(urls.PROCESS_API_URL, params);

/* dashboard */

export const convertLocationParamsToFetchParams = (
  query: ProcessDashboardLocationParamsType
): ProcessDashboardFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE,
  search: query.search,
  ordering: query.ordering,
  team_id: query.team_id !== '-1' ? query.team_id : undefined,
  in_no_teams: query.team_id === '-1' ? '1' : undefined,
  exclude_team_id: query.exclude_team_id,
  tag: query.tag != null ? query.tag : undefined,
  visible_to_user:
    query.visible_to_user != null ? query.visible_to_user : undefined,
  filter: query.filter,
  folder: query.folder,
});

export const fetchProcesses =
  (fetchParams: ProcessDashboardFetchParamsType) => (dispatch: any) =>
    requestProcesses(fetchParams).then(
      (response) =>
        dispatch(receiveProcessDashboardList(fetchParams, response)),
      request.createErrorHandler(gt('Unable to load the processes'), dispatch)
    );

export const fetchProcessesForDashboard = limitRequests(
  (locationParams: ProcessDashboardLocationParamsType) =>
    (dispatch: SimpleDispatch) => {
      dispatch(requestProcessDashboardList(locationParams));
      const fetchParams = convertLocationParamsToFetchParams(locationParams);
      return ((dispatch: any): FuncDispatch)(fetchProcesses(fetchParams));
    }
);

export const fetchProcess =
  ({url}: {url: string}) =>
  (dispatch: SimpleDispatch) => {
    return request
      .get(url)
      .then((data) => dispatch(receiveProcessDetail(data)))
      .catch(
        request.createErrorHandler(gt('Unable to fetch process'), dispatch)
      );
  };

export const fetchProcessDetailByHashid =
  (hashid: string) =>
  (dispatch: FuncDispatch): Promise<any> => {
    const url = urls.PROCESS_HASHID_API_URL + `${hashid}/`;
    return dispatch(fetchProcess({url}));
  };

export const createProcess =
  (data: CreateNewDocProps) => (dispatch: SimpleDispatch) =>
    request
      .postJson(urls.PROCESS_API_URL, {
        steps_blocked_at: moment().utc().format(),
        ...data,
      })
      .then((createdProcess) => {
        dispatch(showNotification(gt('Created process %s', data.name)));
        return createdProcess;
      })
      .catch(
        request.createErrorHandler(
          gt('Unable to create process %s', data.name),
          dispatch
        )
      );

export const deleteProcess =
  (process: {url: string, name: string}) => (dispatch: FuncDispatch) =>
    request
      .del(process.url)
      .then(() =>
        dispatch(
          showNotification(gt('Process %s moved to Recycle Bin', process.name))
        )
      )
      .catch(
        request.createErrorHandler(
          gt('Unable to delete process %s', process.name),
          dispatch
        )
      );

export const RECEIVE_PROCESS_VERSION_DETAIL = 'RECEIVE_PROCESS_VERSION_DETAIL';

type ReceiveProcessyVersionDetailActionType = {
  type: 'RECEIVE_PROCESS_VERSION_DETAIL',
  version: ProcessVersionType,
};
const receiveProcessVersionDetail = (
  version: ProcessVersionType
): ReceiveProcessyVersionDetailActionType => ({
  type: RECEIVE_PROCESS_VERSION_DETAIL,
  version: {
    ...version,
    description: getDescription(version),
  },
});
export const getProcessVersionByURL =
  (url: string) => (dispatch: SimpleDispatch) =>
    get(url).then(
      (version: ProcessVersionType) =>
        dispatch(receiveProcessVersionDetail(version)).version,
      createErrorHandler(gt('Could not load process version.'), dispatch)
    );

export const getProcessVersionByID = limitRequests(
  (id: number | string) => (dispatch: FuncDispatch) =>
    dispatch(getProcessVersionByURL(`${PROCESS_VERSIONS_API}${id}/`))
);
/* process version list */

export const RECEIVE_PROCESS_VERSIONS_LIST = 'RECEIVE_PROCESS_VERSIONS_LIST';
export const receiveProcessVersionsList = (
  processId: number | string,
  data: any
) => ({
  type: RECEIVE_PROCESS_VERSIONS_LIST,
  processId,
  data,
});

export const fetchProcessVersions = limitRequests(
  (
      processId: number,
      page: string = '1',
      page_size: number = DEFAULT_PAGE_SIZE
    ) =>
    (dispatch: SimpleDispatch) => {
      const params = {
        version_object_id: processId,
        page,
        page_size,
      };

      return get(PROCESS_VERSIONS_API, params).then(
        (data) => dispatch(receiveProcessVersionsList(processId, data)),
        createErrorHandler(gt('Could not fetch process versions.'), dispatch)
      );
    }
);

export const fetchProcessLatestVersion =
  (processId: number) => (dispatch: FuncDispatch) => {
    const errorMessage = gt('Could not fetch the latest version');

    return request
      .get(urls.PROCESS_VERSIONS_API, {
        version_object_id: processId,
        page_size: 1,
        page: '1',
      })
      .then((response) => {
        if (response.results.length === 0) {
          throw Error(`No latest found for version id ${processId}`);
        }
        return response.results[0];
      }, request.createErrorHandler(errorMessage, dispatch));
  };

/* process version update */

export const updateProcessVersion =
  (url: string, data: {}) => (dispatch: SimpleDispatch) =>
    patchJson(url, data).then(
      (version) => dispatch(receiveProcessVersionDetail(version)).version,
      createErrorHandler(
        gt('Unable to update process'),
        dispatch,
        showForbiddenMessageHandler(true)
      )
    );

export const approveProcessVersion =
  (
    versionURL: string,
    id: number,
    options: {|notify?: boolean, signoff?: boolean|},
    customNotificationText?: string
  ) =>
  (dispatch: SimpleDispatch) =>
    request
      .postJson(urls.PROCESS_VERSION_APPROVE_API_URL, {
        url: versionURL,
        ...options,
      })
      .then((data) => {
        dispatch(
          showNotification(
            customNotificationText != null
              ? customNotificationText
              : options.notify
              ? gt('Approved. Teammates will be notified')
              : gt('This version is now approved and live.')
          )
        );
        dispatch(receiveProcessDetail(data));
        return dispatch(fetchProcessVersions(id));
      })
      .catch(
        request.createErrorHandler(
          gt('Could not approve that version.'),
          dispatch,
          {
            '400': (error, dispatch) =>
              dispatch(
                showNotification(
                  extractText(
                    error,
                    'data.non_field_errors[0]',
                    gt('Could not approve that version.')
                  ),
                  'error'
                )
              ),
          }
        )
      );
export const addNewProcedureToProcessVersionContent =
  (
    versionURL: string,
    procedure_name: String,
    after_step: string | void,
    replace_step: string | void
  ) =>
  (dispatch: SimpleDispatch) =>
    request
      .postJson(urls.getProcessVersionAddNewProcedureURL(versionURL), {
        procedure_name,
        after_step,
        replace_step,
      })
      .then((version) => {
        dispatch(showNotification(gt('Procedure created and added')));
        return dispatch(receiveProcessVersionDetail(version)).version;
      })
      .catch(
        request.createErrorHandler(
          gt('Unable to add procedure to process'),
          dispatch,
          {
            '403': (error) => {
              if (error.data.detail === urls.STATUSES.UPGRADE_REQUIRED) {
                return dispatch(
                  showUpgradeModal(UPGRADE_MODALS.PROCEDURE_LIMIT)
                );
              }
            },
          }
        )
      );

export const reorderProcessVersionProcedures =
  ({
    version,
    procedureURL,
    targetURL,
  }: {
    version: ProcessVersionType,
    procedureURL: string,
    targetURL?: string,
  }) =>
  (dispatch: SimpleDispatch) =>
    request
      .patchJson(version.url + 'order/', {
        procedure: procedureURL,
        target: targetURL,
      })
      .then((data) => dispatch(receiveProcessVersionDetail(data)).version)
      .catch(
        request.createErrorHandler(
          gt('Unable to reorder the procedures'),
          dispatch
        )
      );
